<template>
    <div
        v-if="isDisplayed"
        ref="elemRef"
        class="selection-list-tile"
        :class="itemClass"
        @click="handleClick"
    >
        <div
            class="grid"
            :class="{ 'navigation-info': item?.navigationOption != null, 'no-icon': !item?.icon }"
        >
            <Position
                v-if="item?.navigationOption"
                x="center"
                y="center"
            >
                <CheckTrigger :model-value="item?.navigationOption?.selected" />
            </Position>

            <Position
                v-if="item?.icon"
                x="center"
                y="center"
            >
                <img
                    class="item-icon"
                    :src="item?.icon"
                    alt="icon"
                />
            </Position>

            <Position
                x="start"
                y="center"
            >
                <div>
                    <Words title>{{ item?.label }}</Words>
                    <Words
                        v-if="item?.description"
                        paragraph
                        dense
                        twoline
                    >
                        {{ item?.description }}
                    </Words>
                </div>
            </Position>

            <Position
                x="end"
                y="center"
                :class="{ arrow: !item?.rightIcon }"
            >
                <transition
                    name="scale"
                    mode="out-in"
                >
                    <img
                        v-if="item?.rightIcon"
                        class="item-icon"
                        :src="item?.rightIcon"
                        alt="right-icon"
                    />
                    <ArrowRight
                        v-else
                        :size="10"
                    />
                </transition>
            </Position>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import Position from 'widget-components/components/Position'
import Words from 'widget-components/components/Words'
import ArrowRight from 'widget-components/assets/svg/ArrowRight.vue'
import CheckTrigger from 'widget-components/components/InputCheckbox/CheckTrigger.vue'
import type { SelectionListItem } from 'widget-components/integrations/CustomPage/types'
import useConditions from 'widget-components/composables/conditions/useConditions'

const props = defineProps<{
    item: SelectionListItem
    loading?: boolean
}>()

const itemId = computed(() => {
    return props.item.id || ''
})

const { isDisplayed, isDisabled } = useConditions(itemId)

const elemRef = ref<HTMLElement | undefined>()

const hasItemGap = computed(() => {
    if (!elemRef.value) {
        return false
    }

    const val = getComputedStyle(elemRef.value)
        .getPropertyValue('--tl-container-item-gap')
        .trim()

    if (!val || val === '0' || val === '0px') {
        return false
    }

    return true
})

const disabled = computed(() => props.item?.navigationOption?.disabled || isDisabled.value)

const itemClass = computed(() => ({
    loading: props.loading,
    active: props.item?.navigationOption?.active,
    disabled: disabled.value,
    pill: hasItemGap.value,
}))

const emit = defineEmits(['click'])

const handleClick = () => {
    if (props.loading || disabled.value) return
    emit('click')
}
</script>

<style lang="sass" scoped>
@import './SelectionListTile.sass'
</style>
